import React, { useCallback, useState } from 'react';
import {
  Service,
  IndicatorHeaderList,
  IndicatorWithUpdate,
  IndicatorSectionKey,
} from '../utils';
import StageEmptyState from './StageEmptyState';
import StagesModal from './StagesModal';
import IndicatorCardDetails from '../IndicatorCardDetails';
import RestoreIndicator from './RestoreIndicator';
import { ReportPeriod } from '../../SectionPeriod/utils';

interface StagesProps {
  service: Service;
  services: Service[];
  section: IndicatorHeaderList;
  maxWidth: number;
  questionId: number;
  reportId: number;
  beneficiaryTerm?: string;
  programName?: string;
  reportPeriod: ReportPeriod;
  serviceGoal?: string;
  serviceType?: string;
  intermediaries?: string;
  isReportPassed: boolean;
  handleTextUpdate: (
    sectionKey: IndicatorSectionKey,
    text: string,
    index: number,
  ) => unknown;
  handleIndicatorSeenUpdate: (
    sectionKey: IndicatorSectionKey,
    seen: boolean,
    index: number,
  ) => unknown;
  handleAddIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleDeleteIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  handleRestoreIndicator: (
    sectionKey: IndicatorSectionKey,
    indicator: IndicatorWithUpdate,
    index: number,
  ) => unknown;
  indicators: IndicatorWithUpdate[];
  selectedIndicators: IndicatorWithUpdate[];
  deletedIndicators: IndicatorWithUpdate[];
}

const Stages: React.FC<StagesProps> = ({
  reportId,
  indicators,
  service,
  services,
  serviceType,
  section,
  maxWidth,
  isReportPassed,
  reportPeriod,
  beneficiaryTerm,
  intermediaries,
  serviceGoal,
  programName,
  handleTextUpdate,
  handleAddIndicator,
  handleDeleteIndicator,
  handleIndicatorSeenUpdate,
  handleRestoreIndicator,
  deletedIndicators,
  selectedIndicators,
}) => {
  const [open, setOpen] = useState<boolean>(false);

  const handleOpen = useCallback(() => {
    setOpen(true);
  }, [setOpen]);
  const handleClose = useCallback(() => {
    setOpen(false);
  }, [setOpen]);

  const handleAdd = useCallback(
    (indicator: IndicatorWithUpdate, index: number) => {
      handleAddIndicator(section.sectionKey, indicator, index);
      handleClose();
    },
    [handleAddIndicator, handleClose, section.sectionKey],
  );

  const handleDelete = useCallback(
    (indicator: IndicatorWithUpdate, index: number) => {
      handleDeleteIndicator(section.sectionKey, indicator, index);
    },
    [handleDeleteIndicator, section.sectionKey],
  );

  return (
    <>
      {selectedIndicators.map((s, i) => (
        <IndicatorCardDetails
          reportPeriod={reportPeriod}
          beneficiaryTerm={beneficiaryTerm}
          programName={programName || 'your program'}
          serviceGoal={serviceGoal}
          intermediaries={intermediaries}
          sectionKey={section.sectionKey}
          serviceType={serviceType}
          index={i}
          indicator={s}
          isReportPassed={isReportPassed}
          key={s.id || s.name}
          maxWidth={maxWidth}
          reportId={reportId}
          handleIndicatorTextUpdate={handleTextUpdate}
          handleIndicatorSeenUpdate={handleIndicatorSeenUpdate}
          onDelete={s.lockDelete ? undefined : handleDelete}
        />
      ))}
      {deletedIndicators.map((d, i) => (
        <RestoreIndicator
          index={i}
          key={d.id}
          indicator={d}
          sectionKey={section.sectionKey}
          handleRestoreIndicator={handleRestoreIndicator}
        />
      ))}
      {selectedIndicators.length === 0 && (
        <StageEmptyState sectionKey={section.sectionKey} maxWidth={maxWidth} />
      )}
      {indicators.length !== 0 && (
        <StagesModal
          open={open}
          header={section.text}
          maxWidth={maxWidth}
          sectionKey={section.sectionKey}
          handleAdd={handleAdd}
          handleOpen={handleOpen}
          indicators={indicators}
          selectedIndicators={selectedIndicators}
          handleClose={handleClose}
        />
      )}
    </>
  );
};

export default Stages;
